/**
 * `reconcile-corpus` — FR-15, NFR-09, NFR-06, NFR-07, IR-01. D-B17 (CLM-35).
 *
 * The stored corpus compared against the **authoritative SOW list** — Qfly
 * opportunities carrying a SOW number (Q3 / runbook A4, decided by Gerry
 * 9 Sep 2026). The list is *sourced*, never stored: the request body carries it,
 * the operator CLI reads it from a CSV export, and Postgres holds no register
 * (K5, "no second master"). The comparison is a pure set operation over
 * `clm.contract` (SOW / AMENDMENT, primary party, `upper(btrim(sow_number))`)
 * and the response **is** the artefact — no contract row changes.
 *
 * ## Nine exception kinds, two classes
 *
 * `GAP_KINDS` are the discrepancies that mean the corpus is not reconciled and
 * fail the Phase-6 cutover exit code (Q5): `missing`, `missing_party_unresolvable`,
 * `extra`, `party_mismatch`, `duplicate_stored`, `duplicate_document`. The other
 * three — `list_row_unusable`, `duplicate_in_list`, `stored_without_sow_number` —
 * are *quality* rows: reported, never dropped, but a malformed upstream number is
 * Qfly's defect rather than a CLM gap.
 *
 * `has_gaps` is deliberately **not** a field: it is `totals.gaps > 0`, derived by
 * both consumers, never stored — two fields that could disagree is one too many.
 *
 * ## Why 2,000 rows and not 5,000
 *
 * ~1.25 MiB at 5,000 rows exceeds `CLM_API_MAX_REQUEST_BYTES` (1 MiB default), so
 * the transport would answer `413` before validation could say
 * `validation_failed`. 2,000 stays under the cap and well above the ~700 SOWs in
 * Qfly and the ~600 five-year population; a larger list is the CLI's job.
 */
import { z } from 'zod';
import { ContractKeySchema, CorrelationIdSchema, IsoTimestampUtcSchema, PartyKeySchema, Sha256Schema, } from '../primitives.js';
import { SowCandidateShapeSchema } from './plan-sharepoint-ingest.js';
/* -------------------------------------------------------------------------- */
/* The authoritative list row                                                  */
/* -------------------------------------------------------------------------- */
/**
 * How a list row names its client — exactly one way, mirroring what CLM can
 * resolve against: `party.sor_reference_id` (the Qfly client id — recommended for
 * Qfly exports), `party.sow_initials`, `party.party_key`, or
 * `lower(btrim(cached_display_name))` (the planner's own rule).
 */
export const CLIENT_REF_KINDS = ['sor_reference_id', 'sow_initials', 'party_key', 'display_name'];
export const ClientRefKindSchema = z.enum(CLIENT_REF_KINDS);
export const AuthoritativeSowRowSchema = z
    .object({
    /** Raw, un-normalised. Blank is allowed and becomes `list_row_unusable`. */
    sow_number: z.string().max(200),
    client_ref: z
        .object({
        kind: ClientRefKindSchema,
        value: z.string().min(1).max(200),
    })
        .strict(),
    /** Upstream state, echoed into the exception row's detail. Never interpreted. */
    status: z.string().max(60).nullable(),
    /** Opportunity id or CSV line number — every exception row cites it. */
    source_row_ref: z.string().min(1).max(200),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Request                                                                     */
/* -------------------------------------------------------------------------- */
export const RECONCILE_CORPUS_MAX_LIST_ROWS = 2000;
export const ReconcileCorpusRequestSchema = z
    .object({
    /** e.g. the export file name — lands in the audit row as `source.label`. */
    source_label: z.string().min(1).max(200),
    authoritative_list: z.array(AuthoritativeSowRowSchema).min(1).max(RECONCILE_CORPUS_MAX_LIST_ROWS),
    /** When true the response also lists every matched `(sow_number, party_key, contract_key)`. */
    include_matched: z.boolean().default(false),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Exceptions                                                                  */
/* -------------------------------------------------------------------------- */
export const RECONCILIATION_EXCEPTION_KINDS = [
    'missing',
    'missing_party_unresolvable',
    'extra',
    'party_mismatch',
    'duplicate_stored',
    'duplicate_document',
    'duplicate_in_list',
    'list_row_unusable',
    'stored_without_sow_number',
];
export const ReconciliationExceptionKindSchema = z.enum(RECONCILIATION_EXCEPTION_KINDS);
/** The kinds that fail the cutover exit code (Q5). Order = report order. */
export const GAP_KINDS = [
    'missing',
    'missing_party_unresolvable',
    'extra',
    'party_mismatch',
    'duplicate_stored',
    'duplicate_document',
];
export function isGapKind(kind) {
    return GAP_KINDS.includes(kind);
}
export const RECONCILIATION_REMEDIATIONS = [
    'plan_ingest',
    'run_party_sync',
    'review_duplicate',
    'fix_upstream',
    'manual_review',
    'none',
];
export const ReconciliationRemediationSchema = z.enum(RECONCILIATION_REMEDIATIONS);
export const ReconciliationExceptionSchema = z
    .object({
    kind: ReconciliationExceptionKindSchema,
    /** The list's or the stored row's raw `sow_number`; `null` when neither side has one. */
    sow_number: z.string().max(200).nullable(),
    sow_number_shape: SowCandidateShapeSchema.nullable(),
    party_key: PartyKeySchema.nullable(),
    party_display_name: z.string().max(200).nullable(),
    contract_key: ContractKeySchema.nullable(),
    /** The second contract of a pair (`duplicate_*`), or the mis-filed one (`party_mismatch`). */
    other_contract_key: ContractKeySchema.nullable(),
    source_row_ref: z.string().max(200).nullable(),
    detail: z.string().min(1).max(1000),
    remediation: ReconciliationRemediationSchema,
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Totals, matched rows, the run                                               */
/* -------------------------------------------------------------------------- */
const count = z.number().int().min(0);
export const ReconciliationTotalsSchema = z
    .object({
    list_rows: count,
    list_usable: count,
    stored_sows: count,
    matched: count,
    missing: count,
    missing_party_unresolvable: count,
    extra: count,
    party_mismatch: count,
    duplicate_stored: count,
    duplicate_document: count,
    duplicate_in_list: count,
    list_row_unusable: count,
    stored_without_sow_number: count,
    /** Σ over `GAP_KINDS`. `has_gaps` is this `> 0`, derived — never a field. */
    gaps: count,
})
    .strict();
export const MatchedSowSchema = z
    .object({
    sow_number: z.string().max(200),
    party_key: PartyKeySchema,
    contract_key: ContractKeySchema,
})
    .strict();
export const RECONCILIATION_SOURCE_KINDS = ['request_body', 'csv_file', 'qfly_opportunities', 'fake'];
export const ReconciliationSourceKindSchema = z.enum(RECONCILIATION_SOURCE_KINDS);
export const ReconciliationSourceSchema = z
    .object({
    kind: ReconciliationSourceKindSchema,
    label: z.string().min(1).max(200),
    /** sha256 of the canonical sorted `sow_number|client_ref` lines — two runs over the same list prove it. */
    list_sha256: Sha256Schema,
})
    .strict();
export const ReconciliationRunSchema = z
    .object({
    correlation_id: CorrelationIdSchema,
    ran_utc: IsoTimestampUtcSchema,
    source: ReconciliationSourceSchema,
    /**
     * `true` only when the actor's scope is `scope(all)`. A practice-scoped run
     * cannot say the corpus has no gaps: rows outside the practices are invisible
     * to it by construction (NFR-06), and R6 — an unscoped count to say how many —
     * was deliberately not shipped (rule 9).
     */
    scope_complete: z.boolean(),
    /** The `clm.audit_log` row this run committed (R4). `'dry-run'` only from the CLI's `--dry-run`. */
    audit_id: z.string().min(1),
})
    .strict();
export const ReconciliationReportSchema = z
    .object({
    run: ReconciliationRunSchema,
    totals: ReconciliationTotalsSchema,
    /** Stable order: kind (in `RECONCILIATION_EXCEPTION_KINDS` order), sow_number, party_key, contract_key. */
    exceptions: z.array(ReconciliationExceptionSchema),
    /** Empty unless `include_matched`. */
    matched: z.array(MatchedSowSchema),
})
    .strict();
export const ReconcileCorpusResponseSchema = ReconciliationReportSchema;
//# sourceMappingURL=reconcile-corpus.js.map